import type { V2PaidLaunchStore } from "./v2-paid-launch";
import { validateBudgetFundingRecord, validateBudgetFundingTransition } from "./v2-budget-funding";
import { hasVerifiedRetiredUnusedPurchaseHistory, hasVerifiedUnusedRetirement, isVerifiedRetiredUnusedPurchaseAction,
  hasVerifiedCoveredPromptChunkHistory, isVerifiedCoveredPromptChunkAction, safeRetiredUnusedHistoryUpdate,
  hasVerifiedTerminalOnchainPurchaseHistory, hasVerifiedTerminalOnchainHistory,
  isVerifiedTerminalOnchainAppendAction, safeTerminalOnchainHistoryUpdate } from "./v2-retired-unused";
import type { DeploymentReference } from "./public-v2";

const DEFAULT_DATABASE = "botnet-family-v2-paid-launch";
const ATTEMPTS = "attempts";
const ACTIVE = "active";
const SELECTED = "selectedAttempts";
const WALLET_CLAIMS = "walletActionClaims";
const DATABASE_VERSION = 2;
const STORES = [ATTEMPTS, ACTIVE, SELECTED, WALLET_CLAIMS] as const;
const MAX_RECOVERY_RECORDS = 100;
const METHOD_MS = 1_800;
const MAX_BYTES = 512 * 1024;
// Full local records retain the existing 512 KiB per-record input budget; the 128 KiB cap applies only to server JSON.
const MAX_RECOVERY_BYTES = MAX_RECOVERY_RECORDS * MAX_BYTES + 2 + MAX_RECOVERY_RECORDS - 1;
const MAX_NODES = 20_000;
const MAX_DEPTH = 24;
const MAX_ARRAY = 512;
const UUID = /^[0-9a-f-]{36}$/i;
const ADDRESS = /^0x[0-9a-f]{40}$/;
const HASH = /^0x[0-9a-f]{64}$/;
const ERROR_INVALID = "The paid launch storage input is invalid.";
const ERROR_CONFLICT = "This paid launch changed or has inconsistent stored data.";
const ERROR_UNAVAILABLE = "Paid launch storage is unavailable.";
const ERROR_TIMEOUT = "Paid launch storage operation timed out.";
const ERROR_CLOSED = "Paid launch storage adapter is closed.";
const KNOWN_RECORD_KEYS = new Set([
  "attemptId", "idempotencyKey", "namespace", "payer", "deployment", "tierId", "name", "draft", "draftDigest", "phase", "revision",
  "reservation", "pendingReservationId", "observation", "consent", "activeAction", "actionHistory", "createdAtMs", "reservationClaimed", "approvalEvidence",
  "paymentEvidence", "promptProgress", "retiredAt", "finalizedEvidence", "terminalEvidence", "budgetFunding",
]);
const REQUIRED_RECORD_KEYS = [
  "attemptId", "idempotencyKey", "namespace", "payer", "deployment", "tierId", "name", "draft", "draftDigest", "phase", "revision",
  "reservation", "observation", "consent", "activeAction", "actionHistory", "createdAtMs",
] as const;
const IMMUTABLE_FIELDS = ["attemptId", "idempotencyKey", "namespace", "payer", "deployment", "tierId", "name", "draft", "draftDigest", "createdAtMs"] as const;
const RETIRED_PHASES = new Set(["retired_unsent", "retired_unused", "terminal_onchain"]);
const PHASES = new Set(["draft", "reservation_pending", "quoted", "paid", "queued", ...RETIRED_PHASES]);

type StoreResult = { kind: "created" | "existing" | "committed"; record: Record<string, unknown> } | { kind: "conflict" };
type Store = V2PaidLaunchStore & {
  listForPayer(payer: string): Promise<readonly Record<string, unknown>[]>;
  loadSelected(payer: string): Promise<Record<string, unknown> | null>;
  selectAttempt(attemptId: string): Promise<Record<string, unknown>>;
  close(): void;
};
type MethodOperation = {
  cancelled: boolean;
  settled: boolean;
  database: IDBDatabase | null;
  transaction: IDBTransaction | null;
  openRequest: IDBOpenDBRequest | null;
  pendingError: Error | null;
  deadline: number;
  timer: ReturnType<typeof globalThis.setTimeout> | null;
  finish(error: Error | null, value?: unknown): void;
  cancel(error: Error): void;
};
type Body = (transaction: IDBTransaction, attempts: IDBObjectStore, active: IDBObjectStore, selected: IDBObjectStore,
  walletClaims: IDBObjectStore, op: MethodOperation, setResult: (value: unknown) => void) => void;

function fixedError(message: string): Error { return new Error(message); }
function failInput(): never { throw fixedError(ERROR_INVALID); }
function isRecord(value: unknown): value is Record<string, unknown> { return value !== null && typeof value === "object" && !Array.isArray(value); }

function snapshotJson(value: unknown): unknown {
  const budget = { nodes: 0, bytes: 0 };
  const visit = (item: unknown, depth: number): unknown => {
    if (++budget.nodes > MAX_NODES || depth > MAX_DEPTH) failInput();
    if (item === null || typeof item === "boolean") return item;
    if (typeof item === "string") {
      budget.bytes += new TextEncoder().encode(item).length;
      if (budget.bytes > MAX_BYTES) failInput();
      return item;
    }
    if (typeof item === "number") { if (!Number.isFinite(item)) failInput(); return item; }
    if (Array.isArray(item)) {
      if (item.length > MAX_ARRAY || Object.getPrototypeOf(item) !== Array.prototype) failInput();
      const descriptors = Object.getOwnPropertyDescriptors(item);
      const keys = Reflect.ownKeys(descriptors);
      if (keys.length !== item.length + 1 || keys.some((key) => typeof key !== "string" ||
          (key !== "length" && !/^(0|[1-9][0-9]*)$/.test(key)))) failInput();
      const output: unknown[] = [];
      for (let index = 0; index < item.length; index++) {
        const descriptor = descriptors[String(index)];
        if (!descriptor?.enumerable || !("value" in descriptor)) failInput();
        output.push(visit(descriptor.value, depth + 1));
      }
      return output;
    }
    if (typeof item !== "object" || item === null) failInput();
    const prototype = Object.getPrototypeOf(item);
    if (prototype !== Object.prototype && prototype !== null) failInput();
    const descriptors = Object.getOwnPropertyDescriptors(item);
    const keys = Reflect.ownKeys(descriptors);
    if (keys.length > 256 || keys.some((key) => typeof key !== "string" || key === "__proto__" || key === "constructor" || key === "prototype")) failInput();
    const output: Record<string, unknown> = {};
    for (const key of keys as string[]) {
      const descriptor = descriptors[key]!;
      if (!descriptor.enumerable || !("value" in descriptor)) failInput();
      budget.bytes += new TextEncoder().encode(key).length;
      if (budget.bytes > MAX_BYTES) failInput();
      output[key] = visit(descriptor.value, depth + 1);
    }
    return output;
  };
  return visit(value, 0);
}

function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`;
  if (isRecord(value)) return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableJson(value[key])}`).join(",")}}`;
  return JSON.stringify(value) ?? "null";
}
function sameData(left: unknown, right: unknown): boolean { return stableJson(left) === stableJson(right); }
function hasExactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  return Object.keys(value).length === keys.length && Object.keys(value).every((key) => keys.includes(key));
}
function validDeployment(value: unknown): value is Record<string, unknown> {
  if (!isRecord(value) || !hasExactKeys(value, ["schemaVersion", "protocol", "chainId", "registryAddress", "deploymentBlockNumber", "deploymentBlockHash", "deploymentTransactionHash", "registryRuntimeCodeHash", "sourceCommitment"])) return false;
  return value.schemaVersion === 1 && value.protocol === "registry-v2" && value.chainId === 8453 &&
    typeof value.registryAddress === "string" && ADDRESS.test(value.registryAddress) && value.registryAddress !== `0x${"0".repeat(40)}` && value.registryAddress === value.registryAddress.toLowerCase() &&
    typeof value.deploymentBlockNumber === "string" && /^(0|[1-9][0-9]*)$/.test(value.deploymentBlockNumber) && Number.isSafeInteger(Number(value.deploymentBlockNumber)) &&
    typeof value.deploymentBlockHash === "string" && HASH.test(value.deploymentBlockHash) && value.deploymentBlockHash === value.deploymentBlockHash.toLowerCase() &&
    typeof value.deploymentTransactionHash === "string" && HASH.test(value.deploymentTransactionHash) && value.deploymentTransactionHash === value.deploymentTransactionHash.toLowerCase() &&
    typeof value.registryRuntimeCodeHash === "string" && HASH.test(value.registryRuntimeCodeHash) && value.registryRuntimeCodeHash === value.registryRuntimeCodeHash.toLowerCase() &&
    typeof value.sourceCommitment === "string" && /^[0-9a-f]{64}$/.test(value.sourceCommitment);
}
function validateReservation(value: unknown, deployment: Record<string, unknown>): boolean {
  if (value === null) return true;
  if (!isRecord(value) || value.schemaVersion !== 2 || typeof value.reservationId !== "string" || !HASH.test(value.reservationId) || value.reservationId !== value.reservationId.toLowerCase()) return false;
  if (value.kind === "state") return hasExactKeys(value, ["kind", "schemaVersion", "reservationId", "state"]) && ["paid", "converted", "released"].includes(String(value.state));
  if (value.kind !== "voucher" || !hasExactKeys(value, ["kind", "schemaVersion", "reservationId", "state", "voucher", "signature", "deployment"])) return false;
  if (!isRecord(value.voucher) || value.voucher.reservationId !== value.reservationId ||
      typeof value.signature !== "string" || !/^0x[0-9a-f]{130}$/.test(value.signature) || value.signature !== value.signature.toLowerCase() ||
      !sameData(value.deployment, deployment) || !["quoted", "paid"].includes(String(value.state))) return false;
  return true;
}
function validateRecord(value: unknown): value is Record<string, unknown> {
  if (!isRecord(value) || REQUIRED_RECORD_KEYS.some((key) => !Object.hasOwn(value, key)) ||
      Object.keys(value).some((key) => !KNOWN_RECORD_KEYS.has(key))) return false;
  const deployment = value.deployment;
  if (!validDeployment(deployment) || typeof value.attemptId !== "string" || !UUID.test(value.attemptId) ||
      typeof value.idempotencyKey !== "string" || !UUID.test(value.idempotencyKey) ||
      typeof value.payer !== "string" || !ADDRESS.test(value.payer) || value.payer === `0x${"0".repeat(40)}` || value.payer !== value.payer.toLowerCase() ||
      value.namespace !== `${deployment.chainId}:${deployment.registryAddress}` ||
      typeof value.tierId !== "number" || !Number.isSafeInteger(value.tierId) || value.tierId < 0 || value.tierId >= 2 ** 32 ||
      typeof value.name !== "string" || value.name.length < 1 || value.name.length > 48 || !/^[A-Za-z0-9 _.-]+$/.test(value.name) ||
      !isRecord(value.draft) || !hasExactKeys(value.draft, ["additionalPrompt", "systemPrompt"]) || typeof value.draft.additionalPrompt !== "string" || !isRecord(value.draft.systemPrompt) ||
      typeof value.draftDigest !== "string" || !HASH.test(value.draftDigest) || value.draftDigest !== value.draftDigest.toLowerCase() ||
      typeof value.phase !== "string" || !PHASES.has(value.phase) ||
      typeof value.revision !== "number" || !Number.isSafeInteger(value.revision) || value.revision < 0 ||
      typeof value.createdAtMs !== "number" || !Number.isSafeInteger(value.createdAtMs) || value.createdAtMs < 0 ||
      !Array.isArray(value.actionHistory) || value.actionHistory.length > MAX_ARRAY ||
      (value.reservationClaimed !== undefined && typeof value.reservationClaimed !== "boolean") || !validateReservation(value.reservation, deployment) ||
      (value.pendingReservationId !== undefined && value.pendingReservationId !== null &&
        (typeof value.pendingReservationId !== "string" || !HASH.test(value.pendingReservationId) ||
          value.pendingReservationId !== value.pendingReservationId.toLowerCase() || value.pendingReservationId === `0x${"0".repeat(64)}` ||
          value.phase !== "reservation_pending" || value.reservationClaimed !== true || value.activeAction !== null ||
          !Array.isArray(value.actionHistory) || value.actionHistory.length !== 0 || value.consent !== null ||
          value.approvalEvidence != null || value.paymentEvidence != null || isRecord(value.budgetFunding) ||
          (value.reservation !== null && !(isRecord(value.reservation) && value.reservation.kind === "state" &&
            value.reservation.state === "released" && value.reservation.reservationId === value.pendingReservationId)))) ||
      (value.budgetFunding !== undefined && (!validateBudgetFundingRecord(value.budgetFunding, deployment as unknown as DeploymentReference) ||
        value.budgetFunding.target.attemptId !== value.attemptId || value.budgetFunding.payer !== value.payer ||
        !isRecord(value.paymentEvidence) || String(value.paymentEvidence.agentId) !== value.budgetFunding.agentId)) ||
      (value.phase === "retired_unused" && !hasVerifiedUnusedRetirement(value)) ||
      (value.phase === "terminal_onchain" && !hasVerifiedTerminalOnchainHistory(value))) return false;
  return true;
}
function activeKey(namespace: string, payer: string): string { return `${namespace}:${payer}`; }
function validPointer(value: unknown, key: string): value is { key: string; attemptId: string } {
  return isRecord(value) && hasExactKeys(value, ["key", "attemptId"]) && value.key === key && typeof value.attemptId === "string" && UUID.test(value.attemptId);
}
function isRetired(record: Record<string, unknown>): boolean { return RETIRED_PHASES.has(String(record.phase)); }
function isFreshDraft(record: Record<string, unknown>): boolean {
  const noEvidence = (key: string) => record[key] === undefined || record[key] === null;
  return record.phase === "draft" && record.revision === 0 && record.reservation === null && record.observation === null &&
    record.consent === null && record.activeAction === null && Array.isArray(record.actionHistory) && record.actionHistory.length === 0 &&
    (record.reservationClaimed === undefined || record.reservationClaimed === false) &&
    ["approvalEvidence", "paymentEvidence", "promptProgress", "retiredAt", "finalizedEvidence"].every(noEvidence);
}
function sameBoundReservation(current: Record<string, unknown>, next: Record<string, unknown>): boolean {
  const before = current.reservation; const after = next.reservation;
  if (before === null) return true;
  if (!isRecord(before) || !isRecord(after) || before.reservationId !== after.reservationId) return false;
  if (before.kind === "voucher") {
    return after.kind === "voucher" && sameData(before.voucher, after.voucher) && before.signature === after.signature && sameData(before.deployment, after.deployment);
  }
  return before.kind === "state" && (after.kind === "state" || after.kind === "voucher");
}
function samePendingReservationMarker(current: Record<string, unknown>, next: Record<string, unknown>): boolean {
  const before = current.pendingReservationId == null ? null : current.pendingReservationId;
  const after = next.pendingReservationId == null ? null : next.pendingReservationId;
  if (before === after) return true;
  if (before === null && typeof after === "string") {
    return current.phase === "reservation_pending" && next.phase === "reservation_pending" && current.reservation === null &&
      (next.reservation === null || (isRecord(next.reservation) && next.reservation.kind === "state" &&
        next.reservation.state === "released" && next.reservation.reservationId === after));
  }
  if (typeof before === "string" && after === null) {
    return current.phase === "reservation_pending" && current.reservation === null && next.phase === "quoted" && isRecord(next.reservation) &&
      next.reservation.kind === "voucher" && next.reservation.reservationId === before;
  }
  return false;
}
function immutableMatches(current: Record<string, unknown>, next: Record<string, unknown>): boolean {
  const fundingTransition = current.budgetFunding === undefined && next.budgetFunding === undefined
    ? true : validateBudgetFundingTransition(current.budgetFunding ?? null, next.budgetFunding ?? null);
  return IMMUTABLE_FIELDS.every((key) => sameData(current[key], next[key])) && sameBoundReservation(current, next) &&
    samePendingReservationMarker(current, next) && fundingTransition;
}

export function createV2PaidLaunchIndexedDbStore(options: { factory?: IDBFactory; databaseName?: string } = {}): Store {
  const databaseName = options.databaseName ?? DEFAULT_DATABASE;
  if (typeof databaseName !== "string" || !/^[A-Za-z0-9][A-Za-z0-9._-]{0,95}$/.test(databaseName)) throw fixedError(ERROR_INVALID);
  const idb = options.factory ?? (typeof indexedDB === "undefined" ? undefined : indexedDB);
  let closed = false;
  const operations = new Set<MethodOperation>();

  function isLive(op: MethodOperation): boolean {
    if (op.cancelled || op.settled) return false;
    if (closed) { op.cancel(fixedError(ERROR_CLOSED)); return false; }
    if (globalThis.performance.now() >= op.deadline) { op.cancel(fixedError(ERROR_TIMEOUT)); return false; }
    return true;
  }

  function transaction(op: MethodOperation, database: IDBDatabase, mode: IDBTransactionMode, body: Body): void {
    if (!isLive(op)) { database.close(); return; }
    let tx: IDBTransaction;
    try {
      tx = mode === "readwrite" ? database.transaction([...STORES], mode, { durability: "strict" }) : database.transaction([...STORES], mode);
    } catch { op.finish(fixedError(ERROR_UNAVAILABLE)); return; }
    op.transaction = tx;
    let result: unknown;
    tx.oncomplete = () => { if (isLive(op)) op.finish(null, result); };
    tx.onabort = () => { if (!op.settled && isLive(op)) op.finish(op.pendingError ?? fixedError(ERROR_UNAVAILABLE)); };
    tx.onerror = () => { /* the default IndexedDB action aborts the transaction */ };
    try {
      if (database.version !== DATABASE_VERSION || database.objectStoreNames.length !== STORES.length ||
          STORES.some((name) => !database.objectStoreNames.contains(name))) throw fixedError(ERROR_UNAVAILABLE);
      const attempts = tx.objectStore(ATTEMPTS); const active = tx.objectStore(ACTIVE);
      const selected = tx.objectStore(SELECTED); const walletClaims = tx.objectStore(WALLET_CLAIMS);
      if (attempts.keyPath !== "attemptId" || active.keyPath !== "key" || selected.keyPath !== "key" || walletClaims.keyPath !== "payer" ||
          !attempts.indexNames.contains("payer") || attempts.index("payer").keyPath !== "payer") throw fixedError(ERROR_UNAVAILABLE);
      body(tx, attempts, active, selected, walletClaims, op, (value) => { result = value; });
    } catch (error) {
      op.pendingError = error instanceof Error && error.message === ERROR_CONFLICT ? error : fixedError(ERROR_UNAVAILABLE);
      try { tx.abort(); } catch { op.finish(op.pendingError); }
    }
  }

  function run(mode: IDBTransactionMode, body: Body): Promise<unknown> {
    if (closed) return Promise.reject(fixedError(ERROR_CLOSED));
    if (!idb) return Promise.reject(fixedError(ERROR_UNAVAILABLE));
    return new Promise((resolve, reject) => {
      let settle!: (error: Error | null, value?: unknown) => void;
      const op: MethodOperation = {
        cancelled: false, settled: false, database: null, transaction: null, openRequest: null, pendingError: null, timer: null,
        deadline: globalThis.performance.now() + METHOD_MS,
        finish(error, value) {
          if (op.settled) return;
          if (!error && !op.cancelled && globalThis.performance.now() >= op.deadline) { op.cancel(fixedError(ERROR_TIMEOUT)); return; }
          op.settled = true; op.cancelled = true; if (op.timer !== null) clearTimeout(op.timer); operations.delete(op);
          const db = op.database; op.database = null;
          if (db) { db.onversionchange = null; try { db.close(); } catch { /* already closed */ } }
          settle(error, value);
        },
        cancel(error) {
          if (op.settled) return;
          op.cancelled = true; op.pendingError = error;
          try { op.transaction?.abort(); } catch { /* transaction already inactive */ }
          op.finish(error);
        },
      };
      settle = (error, value) => { if (error) reject(error); else resolve(value); };
      operations.add(op);
      op.timer = globalThis.setTimeout(() => op.cancel(fixedError(ERROR_TIMEOUT)), Math.max(0, op.deadline - globalThis.performance.now()));
      if (!isLive(op)) return;
      let request: IDBOpenDBRequest;
      try { request = idb.open(databaseName, DATABASE_VERSION); }
      catch { op.finish(fixedError(ERROR_UNAVAILABLE)); return; }
      op.openRequest = request;
      request.onupgradeneeded = (event) => {
        const upgrade = request.transaction;
        if (!isLive(op)) { try { upgrade?.abort(); } catch { /* terminal open follows */ } return; }
        try {
          if (!upgrade) throw fixedError(ERROR_UNAVAILABLE);
          op.transaction = upgrade;
          if (event.oldVersion === 0) {
            if (request.result.objectStoreNames.length !== 0 || !isLive(op)) throw fixedError(ERROR_UNAVAILABLE);
            const attempts = request.result.createObjectStore(ATTEMPTS, { keyPath: "attemptId" });
            attempts.createIndex("payer", "payer", { unique: false });
            if (!isLive(op)) { try { upgrade.abort(); } catch { /* terminal open follows */ } return; }
            request.result.createObjectStore(ACTIVE, { keyPath: "key" });
          } else if (event.oldVersion === 1) {
            if (request.result.objectStoreNames.length !== 2 || !request.result.objectStoreNames.contains(ATTEMPTS) ||
                !request.result.objectStoreNames.contains(ACTIVE) || !isLive(op)) throw fixedError(ERROR_UNAVAILABLE);
            const attempts = upgrade.objectStore(ATTEMPTS); const active = upgrade.objectStore(ACTIVE);
            if (attempts.keyPath !== "attemptId" || active.keyPath !== "key" || attempts.indexNames.contains("payer")) throw fixedError(ERROR_UNAVAILABLE);
            attempts.createIndex("payer", "payer", { unique: false });
          } else throw fixedError(ERROR_UNAVAILABLE);
          if (!isLive(op)) { try { upgrade.abort(); } catch { /* terminal open follows */ } return; }
          request.result.createObjectStore(SELECTED, { keyPath: "key" });
          request.result.createObjectStore(WALLET_CLAIMS, { keyPath: "payer" });
        } catch {
          op.pendingError = fixedError(ERROR_UNAVAILABLE);
          try { upgrade?.abort(); } catch { op.finish(op.pendingError); }
        }
      };
      request.onblocked = () => { /* bounded by this operation's absolute deadline */ };
      request.onerror = () => { if (isLive(op)) op.finish(op.pendingError ?? fixedError(ERROR_UNAVAILABLE)); };
      request.onsuccess = () => {
        const database = request.result;
        if (!isLive(op)) { try { database.close(); } catch { /* already closed */ } return; }
        op.database = database;
        database.onversionchange = () => { if (isLive(op)) op.cancel(fixedError(ERROR_UNAVAILABLE)); };
        transaction(op, database, mode, body);
      };
    });
  }

  function requestSuccess<T>(request: IDBRequest<T>, op: MethodOperation, handler: (value: T) => void): void {
    request.addEventListener("success", () => {
      if (!isLive(op)) return;
      try { handler(request.result); }
      catch (error) {
        op.pendingError = error instanceof Error && error.message === ERROR_CONFLICT ? error : fixedError(ERROR_CONFLICT);
        try { op.transaction?.abort(); } catch { op.finish(op.pendingError); }
      }
    }, { once: true });
  }
  function cursorSuccess(request: IDBRequest<IDBCursorWithValue | null>, op: MethodOperation,
    handler: (value: IDBCursorWithValue | null) => void): void {
    request.addEventListener("success", () => {
      if (!isLive(op)) return;
      try { handler(request.result); }
      catch (error) {
        op.pendingError = error instanceof Error && error.message === ERROR_CONFLICT ? error : fixedError(ERROR_CONFLICT);
        try { op.transaction?.abort(); } catch { op.finish(op.pendingError); }
      }
    });
  }
  function snapshotStored(value: unknown): Record<string, unknown> {
    const copy = snapshotJson(value);
    if (!validateRecord(copy)) throw fixedError(ERROR_CONFLICT);
    return copy;
  }
  function pointerOrConflict(value: unknown, key: string): { key: string; attemptId: string } {
    if (!validPointer(value, key)) throw fixedError(ERROR_CONFLICT);
    return value;
  }

  function hasUnresolvedWalletActivity(record: Record<string, unknown>): boolean {
    if (record.activeAction !== null) return true;
    const paidEvidence = isRecord(record.paymentEvidence) ? record.paymentEvidence.transactionHash : null;
    const approvalEvidence = isRecord(record.approvalEvidence) ? record.approvalEvidence.transactionHash : null;
    if (Array.isArray(record.actionHistory)) {
      for (const item of record.actionHistory) {
        if (!isRecord(item)) return true;
        if (item.status === "dispatching") return true;
        if (item.status === "uncertain") {
          if (isVerifiedTerminalOnchainAppendAction(record, item)) continue;
          if (isVerifiedRetiredUnusedPurchaseAction(record, item)) continue;
          if (isVerifiedCoveredPromptChunkAction(record, item)) continue;
          return true;
        }
        if (item.status === "failed" && item.enteredDispatch === false && item.returnedHash === null) continue;
        if (item.status === "failed" && item.enteredDispatch === true && typeof item.returnedHash === "string") continue;
        if (item.status === "submitted") {
          if (isVerifiedTerminalOnchainAppendAction(record, item)) continue;
          if (isVerifiedRetiredUnusedPurchaseAction(record, item)) continue;
          if (item.kind === "purchase" && item.returnedHash === paidEvidence) continue;
          if (item.kind === "approve" && item.returnedHash === approvalEvidence) continue;
          if (item.kind === "chunk" && isVerifiedCoveredPromptChunkAction(record, item)) continue;
          return true;
        }
        if (item.status !== "failed" && item.status !== "confirmed") return true;
      }
    }
    if (isRecord(record.budgetFunding)) {
      const funding = record.budgetFunding;
      if (funding.activeAction !== null) return true;
      const fundingEvidence = [funding.approvalEvidence, funding.depositEvidence, funding.returnedEvidence]
        .filter(isRecord).map((evidence) => evidence.transactionHash);
      if (Array.isArray(funding.actionHistory)) {
        for (const action of funding.actionHistory) {
          if (!isRecord(action)) return true;
          if (action.status === "uncertain") return true;
          if (action.status === "submitted" && !fundingEvidence.includes(action.hash)) return true;
          if (action.status !== "submitted" && action.status !== "confirmed") return true;
        }
      }
    }
    return false;
  }

  function collectPayerRecords(attempts: IDBObjectStore, payer: string, op: MethodOperation,
    done: (records: Record<string, unknown>[]) => void): void {
    const index = attempts.index("payer");
    const request = index.openCursor(IDBKeyRange.only(payer));
    const records: Record<string, unknown>[] = [];
    cursorSuccess(request, op, (cursor) => {
      if (!cursor) { done(records); return; }
      const row = snapshotStored(cursor.value);
      if (row.payer !== payer || records.length >= MAX_RECOVERY_RECORDS) throw fixedError(ERROR_CONFLICT);
      records.push(row);
      cursor.continue();
    });
  }

  function updateWalletClaim(attempts: IDBObjectStore, walletClaims: IDBObjectStore, payer: string,
    current: Record<string, unknown>, next: Record<string, unknown>, op: MethodOperation, apply: () => void,
    refuseNewActivity: () => void): void {
    // A retained terminal attempt can receive late evidence for activity it
    // already owns, but must never originate a new wallet action. Otherwise a
    // retired-row CAS could manufacture a fresh claim after retirement.
    if (isRetired(current) && !hasUnresolvedWalletActivity(current) && hasUnresolvedWalletActivity(next)) {
      refuseNewActivity(); return;
    }
    collectPayerRecords(attempts, payer, op, (rows) => {
      const currentIndex = rows.findIndex((row) => row.attemptId === current.attemptId);
      if (currentIndex < 0) throw fixedError(ERROR_CONFLICT);
      const proposed = rows.map((row, index) => index === currentIndex ? next : row);
      const unresolved = proposed.filter(hasUnresolvedWalletActivity);
      const otherUnresolved = rows.some((row, index) => index !== currentIndex && hasUnresolvedWalletActivity(row));
      if (!hasUnresolvedWalletActivity(current) && hasUnresolvedWalletActivity(next) && otherUnresolved) {
        refuseNewActivity(); return;
      }
      const claimRequest = walletClaims.get(payer);
      requestSuccess(claimRequest, op, (rawClaim) => {
        let claim: { payer: string; attemptId: string } | null = null;
        let repairedRetiredOwner = false;
        if (rawClaim !== undefined) {
          if (!isRecord(rawClaim) || !hasExactKeys(rawClaim, ["payer", "attemptId"]) || rawClaim.payer !== payer ||
              typeof rawClaim.attemptId !== "string" || !UUID.test(rawClaim.attemptId) || !rows.some((row) => row.attemptId === rawClaim.attemptId)) {
            throw fixedError(ERROR_CONFLICT);
          }
          claim = rawClaim as { payer: string; attemptId: string };
          const originalOwner = rows.find((row) => row.attemptId === claim!.attemptId);
          if (!originalOwner) throw fixedError(ERROR_CONFLICT);
          if (!hasUnresolvedWalletActivity(originalOwner)) {
            if (!hasVerifiedRetiredUnusedPurchaseHistory(originalOwner) && !hasVerifiedCoveredPromptChunkHistory(originalOwner) &&
                !hasVerifiedTerminalOnchainPurchaseHistory(originalOwner)) {
              throw fixedError(ERROR_CONFLICT);
            }
            repairedRetiredOwner = true;
            claim = null;
          }
        }
        if (unresolved.length === 0) {
          if (claim || repairedRetiredOwner) walletClaims.delete(payer);
        } else {
          const owner = claim ? unresolved.find((row) => row.attemptId === claim!.attemptId) ?? unresolved[0]! : unresolved[0]!;
          walletClaims.put({ payer, attemptId: String(owner.attemptId) });
        }
        apply();
      });
    });
  }

  const store: Store = {
    create(input, options?: { intent?: "new" }) {
      let record: Record<string, unknown>;
      let createDistinct = false;
      try {
        if (options !== undefined) {
          const optionCopy = snapshotJson(options);
          if (!isRecord(optionCopy) || !hasExactKeys(optionCopy, ["intent"]) || optionCopy.intent !== "new") failInput();
          createDistinct = true;
        }
        const copy = snapshotJson(input);
        if (!validateRecord(copy) || !isFreshDraft(copy)) failInput();
        record = copy;
      } catch { return Promise.reject(fixedError(ERROR_INVALID)); }
      const key = activeKey(String(record.namespace), String(record.payer));
      return run("readwrite", (_tx, attempts, active, _selected, _walletClaims, op, setResult) => {
        const pointerRequest = active.get(key);
        requestSuccess(pointerRequest, op, (rawPointer) => {
          if (rawPointer !== undefined) {
            const pointer = pointerOrConflict(rawPointer, key);
            const currentRequest = attempts.get(pointer.attemptId);
            requestSuccess(currentRequest, op, (rawCurrent) => {
              if (rawCurrent === undefined) throw fixedError(ERROR_CONFLICT);
              const current = snapshotStored(rawCurrent);
              if (current.attemptId !== pointer.attemptId || current.namespace !== record.namespace || current.payer !== record.payer || isRetired(current)) throw fixedError(ERROR_CONFLICT);
              if (createDistinct) addDistinctRecord();
              else setResult({ kind: "existing", record: snapshotStored(current) } satisfies StoreResult);
            });
            return;
          }
          addDistinctRecord();
        });

        function addDistinctRecord() {
          const attemptRequest = attempts.get(String(record.attemptId));
          requestSuccess(attemptRequest, op, (rawExisting) => {
            if (rawExisting !== undefined) { setResult({ kind: "conflict" } satisfies StoreResult); return; }
            if (!isLive(op)) return;
            const add = attempts.add(record);
            requestSuccess(add, op, () => {
              if (!isLive(op)) return;
              const put = active.put({ key, attemptId: record.attemptId });
              requestSuccess(put, op, () => setResult({ kind: "created", record: snapshotStored(record) } satisfies StoreResult));
            });
          });
        }
      });
    },

    load(id) {
      if (typeof id !== "string" || !UUID.test(id)) return Promise.reject(fixedError(ERROR_INVALID));
      return run("readonly", (_tx, attempts, _active, _selected, _walletClaims, op, setResult) => {
        const request = attempts.get(id);
        requestSuccess(request, op, (raw) => setResult(raw === undefined ? null : snapshotStored(raw)));
      }).then((value) => {
        if (value === null || isRecord(value)) return value;
        throw fixedError(ERROR_CONFLICT);
      });
    },

    loadActive(namespace, payer) {
      if (typeof namespace !== "string" || namespace.length > 128 || !/^[0-9]+:0x[0-9a-f]{40}$/.test(namespace) ||
          typeof payer !== "string" || !ADDRESS.test(payer) || payer !== payer.toLowerCase()) return Promise.reject(fixedError(ERROR_INVALID));
      const key = activeKey(namespace, payer);
      return run("readonly", (_tx, attempts, active, _selected, _walletClaims, op, setResult) => {
        const request = active.get(key);
        requestSuccess(request, op, (rawPointer) => {
          if (rawPointer === undefined) { setResult(null); return; }
          const pointer = pointerOrConflict(rawPointer, key);
          const recordRequest = attempts.get(pointer.attemptId);
          requestSuccess(recordRequest, op, (rawRecord) => {
            if (rawRecord === undefined) throw fixedError(ERROR_CONFLICT);
            const record = snapshotStored(rawRecord);
            if (record.attemptId !== pointer.attemptId || record.namespace !== namespace || record.payer !== payer || isRetired(record)) throw fixedError(ERROR_CONFLICT);
            setResult(snapshotStored(record));
          });
        });
      }).then((value) => {
        if (value === null || isRecord(value)) return value;
        throw fixedError(ERROR_CONFLICT);
      });
    },

    listForPayer(payer) {
      if (typeof payer !== "string" || !ADDRESS.test(payer) || payer === `0x${"0".repeat(40)}` || payer !== payer.toLowerCase()) {
        return Promise.reject(fixedError(ERROR_INVALID));
      }
      return run("readonly", (_tx, attempts, _active, _selected, _walletClaims, op, setResult) => {
        const request = attempts.index("payer").openCursor(IDBKeyRange.only(payer));
        const records: Record<string, unknown>[] = [];
        let byteCount = 2;
        cursorSuccess(request, op, (cursor) => {
          if (!cursor) {
            records.sort((left, right) => Number(left.createdAtMs) - Number(right.createdAtMs) || String(left.attemptId).localeCompare(String(right.attemptId)));
            setResult(records);
            return;
          }
          const record = snapshotStored(cursor.value);
          if (record.payer !== payer || records.length >= MAX_RECOVERY_RECORDS) throw fixedError(ERROR_CONFLICT);
          const rowBytes = new TextEncoder().encode(JSON.stringify(record)).length;
          byteCount += rowBytes + (records.length === 0 ? 0 : 1);
          if (byteCount > MAX_RECOVERY_BYTES) throw fixedError(ERROR_CONFLICT);
          records.push(record);
          cursor.continue();
        });
      }).then((value) => {
        if (Array.isArray(value) && value.every(isRecord)) return value as Record<string, unknown>[];
        throw fixedError(ERROR_CONFLICT);
      });
    },

    selectAttempt(attemptId) {
      if (typeof attemptId !== "string" || !UUID.test(attemptId)) return Promise.reject(fixedError(ERROR_INVALID));
      return run("readwrite", (_tx, attempts, _active, selected, _walletClaims, op, setResult) => {
        const request = attempts.get(attemptId);
        requestSuccess(request, op, (raw) => {
          if (raw === undefined) throw fixedError(ERROR_CONFLICT);
          const record = snapshotStored(raw);
          const key = String(record.payer);
          if (!ADDRESS.test(key) || key !== key.toLowerCase()) throw fixedError(ERROR_CONFLICT);
          const saved = selected.put({ key, attemptId });
          requestSuccess(saved, op, () => setResult(snapshotStored(record)));
        });
      }).then((value) => {
        if (isRecord(value)) return value;
        throw fixedError(ERROR_CONFLICT);
      });
    },

    loadSelected(payer) {
      if (typeof payer !== "string" || !ADDRESS.test(payer) || payer === `0x${"0".repeat(40)}` || payer !== payer.toLowerCase()) {
        return Promise.reject(fixedError(ERROR_INVALID));
      }
      return run("readonly", (_tx, attempts, _active, selected, _walletClaims, op, setResult) => {
        const request = selected.get(payer);
        requestSuccess(request, op, (rawSelection) => {
          if (rawSelection === undefined) { setResult(null); return; }
          if (!isRecord(rawSelection) || !hasExactKeys(rawSelection, ["key", "attemptId"]) || rawSelection.key !== payer ||
              typeof rawSelection.attemptId !== "string" || !UUID.test(rawSelection.attemptId)) throw fixedError(ERROR_CONFLICT);
          const attempt = attempts.get(rawSelection.attemptId);
          requestSuccess(attempt, op, (rawRecord) => {
            if (rawRecord === undefined) throw fixedError(ERROR_CONFLICT);
            const record = snapshotStored(rawRecord);
            if (record.payer !== payer) throw fixedError(ERROR_CONFLICT);
            setResult(snapshotStored(record));
          });
        });
      }).then((value) => {
        if (value === null || isRecord(value)) return value;
        throw fixedError(ERROR_CONFLICT);
      });
    },

    compareAndSet(id, revision, inputNext) {
      if (typeof id !== "string" || !UUID.test(id) || !Number.isSafeInteger(revision) || revision < 0) return Promise.reject(fixedError(ERROR_INVALID));
      let next: Record<string, unknown>;
      try { const copy = snapshotJson(inputNext); if (!validateRecord(copy)) return Promise.resolve({ kind: "conflict" } satisfies StoreResult); next = copy; }
      catch { return Promise.reject(fixedError(ERROR_INVALID)); }
      return run("readwrite", (_tx, attempts, active, _selected, walletClaims, op, setResult) => {
        const currentRequest = attempts.get(id);
        requestSuccess(currentRequest, op, (rawCurrent) => {
          if (rawCurrent === undefined) { setResult({ kind: "conflict" } satisfies StoreResult); return; }
          const current = snapshotStored(rawCurrent);
          if (current.attemptId !== id || current.revision !== revision || next.attemptId !== id || next.revision !== revision + 1 ||
              !Number.isSafeInteger(next.revision) || !immutableMatches(current, next) || (isRetired(current) && !isRetired(next)) ||
              !safeRetiredUnusedHistoryUpdate(current, next) || !safeTerminalOnchainHistoryUpdate(current, next)) {
            setResult({ kind: "conflict" } satisfies StoreResult); return;
          }
          if (isRetired(current)) {
            const apply = () => {
              if (!isLive(op)) return;
              const write = attempts.put(next);
              requestSuccess(write, op, () => setResult({ kind: "committed", record: snapshotStored(next) } satisfies StoreResult));
            };
            updateWalletClaim(attempts, walletClaims, String(current.payer), current, next, op, apply,
              () => setResult({ kind: "conflict" } satisfies StoreResult));
            return;
          }
          const pointerKey = activeKey(String(current.namespace), String(current.payer));
          const pointerRequest = active.get(pointerKey);
          requestSuccess(pointerRequest, op, (rawPointer) => {
            const retires = isRetired(next);
            if (rawPointer !== undefined && !validPointer(rawPointer, pointerKey)) {
              setResult({ kind: "conflict" } satisfies StoreResult); return;
            }
            const apply = () => {
              if (!isLive(op)) return;
              const write = attempts.put(next);
              requestSuccess(write, op, () => {
                if (retires && rawPointer !== undefined && rawPointer.attemptId === id && isLive(op)) active.delete(pointerKey);
                setResult({ kind: "committed", record: snapshotStored(next) } satisfies StoreResult);
              });
            };
            const refuseNewActivity = () => setResult({ kind: "conflict" } satisfies StoreResult);
            // The active pointer is only a draft de-duplication hint. A
            // retained attempt remains independently recoverable after that
            // pointer is removed; still run every such write through the
            // atomic payer-wide wallet-activity gate.
            if (rawPointer === undefined || rawPointer.attemptId === id) {
              updateWalletClaim(attempts, walletClaims, String(current.payer), current, next, op, apply, refuseNewActivity); return;
            }
            const pointed = attempts.get(rawPointer.attemptId);
            requestSuccess(pointed, op, (rawPointed) => {
              if (rawPointed === undefined) { setResult({ kind: "conflict" } satisfies StoreResult); return; }
              const pointerRecord = snapshotStored(rawPointed);
              if (pointerRecord.payer !== current.payer || pointerRecord.namespace !== current.namespace || isRetired(pointerRecord)) {
                setResult({ kind: "conflict" } satisfies StoreResult); return;
              }
              updateWalletClaim(attempts, walletClaims, String(current.payer), current, next, op, apply, refuseNewActivity);
            });
          });
        });
      });
    },

    close() {
      if (closed) return;
      closed = true;
      for (const op of [...operations]) op.cancel(fixedError(ERROR_CLOSED));
    },
  };
  return store;
}
